import React from 'react'
import clsx from 'clsx'
import NextLink from 'next/link'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import { Header } from 'app/KinerjaShared/components/Header'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import Steps from 'app/KinerjaTimPenilaiPSPage/pages/Perencanaan/Beranda/Steps'
import CircularProgressV2 from 'app/KinerjaPSPage/components/CircularProgressV2'
import {
  PERENCANAAN_TIM_KINERJA_MENUS,
  PERENCANAAN_PEJABAT_PENILAI_KINERJA_MENUS,
} from 'app/KinerjaTimPenilaiPSPage/constants/perencanaan'
import useBasePath from 'app/KinerjaTimPenilaiPSPage/hooks/useBasePath'
import { useSummary } from 'app/KinerjaTimPenilaiPSPage/hooks/useSummary'
import usePerencanaanList from 'app/KinerjaTimPenilaiPSPage/hooks/usePerencanaanList'

const OptionCards = ({
  isTimKinerjaPengawasSekolah,
  submittedCount,
  notSubmittedCount,
}) => {
  const { basePath } = useBasePath()

  return (
    <>
      {(isTimKinerjaPengawasSekolah
        ? PERENCANAAN_TIM_KINERJA_MENUS
        : PERENCANAAN_PEJABAT_PENILAI_KINERJA_MENUS
      ).map((spec, index) => {
        return (
          <SimpleCard
            key={`card-${index}`}
            className={clsx('space-y-6 p-6 flex-1', spec?.bgColor ?? '')}
          >
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Text variant="heading-sm" color={spec.textColor}>
                  {spec.title}
                </Text>
                {spec.key === 'bulk-approval' && submittedCount > 0 && (
                  <Badge variant="informational">{submittedCount}</Badge>
                )}
                {spec.key === 'not-submitted' && notSubmittedCount > 0 && (
                  <Badge variant="critical">{notSubmittedCount}</Badge>
                )}
              </div>
              <Text variant="body" color={spec.subtitleColor}>
                {spec.subtitle}
              </Text>
            </div>

            <div className="flex">
              <NextLink href={`${basePath}/${spec.path}`} passHref>
                <Button as="a" color={spec.ctaColor}>
                  {spec.ctaText}
                </Button>
              </NextLink>
            </div>
          </SimpleCard>
        )
      })}
    </>
  )
}

type TimPenilaiPSPerencanaanPageProps = {}
const TimPenilaiPSPerencanaanPage: React.FC<
  TimPenilaiPSPerencanaanPageProps
> = ({}) => {
  const {
    basePath,
    isTimKinerjaPengawasSekolahUrl: isTimKinerjaPengawasSekolah,
    isPPKPengawasSekolahUrl: isPPKPengawasSekolah,
  } = useBasePath()

  // Summary API
  const { apiStatus, summary } = useSummary()
  const { data: notSubmittedData, isLoading: isNotSubmittedLoading } =
    usePerencanaanList({
      filter: 'BELUM_DIAJUKAN_PENGAWAS',
    })
  const { data: canBeBulkApprovedData, isLoading: isBulkApprovedLoading } =
    usePerencanaanList({
      filter: 'DAPAT_DISETUJUI_SEKALIGUS',
      isEnabled: isPPKPengawasSekolah,
    })
  const notSubmittedCount = React.useMemo(() => {
    if (!isNotSubmittedLoading) {
      return (notSubmittedData?.data ?? []).length
    }
    return 0
  }, [isNotSubmittedLoading, notSubmittedData?.data])
  const submittedCount = React.useMemo(() => {
    if (!isBulkApprovedLoading) {
      return (canBeBulkApprovedData?.data ?? []).length
    }
    return 0
  }, [canBeBulkApprovedData?.data, isBulkApprovedLoading])

  if (apiStatus === 'loading') {
    return null
  }

  return (
    <div className="pb-20">
      <div className="w-full p-8 mb-10 bg-white">
        <Header title="Perencanaan Kinerja" className="mx-auto print:hidden" />
      </div>

      <div className="pt-4 mx-auto mb-10 max-w-7xl">
        <div className="px-6 space-y-4 rounded-t-lg py-9 bg-surface-selected-default">
          <Text variant="heading-md">
            Periksa Perencanaan dan pantau progres pemeriksaan Anda di sini:
          </Text>
          <div className="flex justify-between space-x-8">
            <OptionCards
              isTimKinerjaPengawasSekolah={isTimKinerjaPengawasSekolah}
              submittedCount={submittedCount}
              notSubmittedCount={notSubmittedCount}
            />
          </div>
        </div>
        <div className="flex items-center justify-between p-6 space-x-8 bg-white border-b rounded-b-lg border-subdued">
          <div className="w-[106px] h-[106px] flex-none">
            <CircularProgressV2
              value={summary?.perencanaan?.done ?? 0}
              maxValue={summary?.perencanaan?.total ?? 0}
              textVariant="heading-sm"
            />
          </div>
          <div className="col-span-10 space-y-3 grow">
            <div className="space-y-1">
              <div>
                <Text variant="heading-lg">
                  Perencanaan sudah disetujui Pejabat Penilai Kinerja
                </Text>
              </div>
            </div>
          </div>

          <div className="flex-none">
            <NextLink
              href={`${basePath}/penilai/perencanaan/daftar-ps/disetujui`}
              passHref
            >
              <Button
                as="a"
                disabled={!Boolean(summary?.perencanaan?.done)}
                className="w-48"
                color="black"
              >
                Cek
              </Button>
            </NextLink>
          </div>
        </div>
      </div>

      <div className="mx-auto space-y-4 max-w-7xl">
        <Text variant="heading-md">Tahap Perencanaan Kinerja</Text>
        <Steps />
      </div>
    </div>
  )
}
export default TimPenilaiPSPerencanaanPage
